import React, { FC, useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MuiContainer from '@mui/material/Container';
import { styled } from '@mui/system';
import MuiGrid from '@mui/material/Grid';
import Button from 'src/components/Button';
import Typography from 'src/components/Typography';
import TextField from 'src/components/TextField';
import translate from '@translate';
import style from './style';
import { checkFormValidity } from '../../utils/formFunctions';
import { clearForgetPassword, forgetPasswordFetch } from '../../resources/login/reducer';
import Loading from 'src/components/Loading/intex';
import appLogo from 'src/utils/appLogo';
import LangSelect from 'src/components/LangSelect';
import Fade from '@mui/material/Fade';
import { RootStoreType } from 'src/types/store';
import { Logo, LogoBlock } from '.';
import { colors } from 'src/themes/default';

const TIMEOUT_STATUS_FORGET_PASSWORD = 10000;

const initData = { email: '' };
const initErrors: { [key: string]: boolean } = {
    email: false,
};

export interface ForgetPasswordPropsType {
    onClose: () => void;
}

export const MessageBlock = styled('div')<{
    type: 'success' | 'error';
}>(({ theme, type }) => ({
    marginTop: '10px',
    width: '100%',
    textAlign: 'center',
    padding: '10px 0px',
    backgroundColor: type === 'error' ? colors.alert_D2 : colors.accent_D2,
}));

const ForgetPassword: FC<ForgetPasswordPropsType> = (props) => {
    const dispatch = useDispatch();
    const loading = useSelector((state: RootStoreType) => state.login.loadingForgetPassword);
    const success = useSelector((state: RootStoreType) => state.login.successForgetPassword);
    const error = useSelector((state: RootStoreType) => state.login.errorForgetPassword);

    const formRef = useRef<HTMLFormElement>(null);
    const [editData, setEditData] = useState(initData);
    const [editErrors, setEditErrors] = useState(initErrors);

    useEffect(() => {
        dispatch(clearForgetPassword());
        return () => {
            dispatch(clearForgetPassword());
        };
    }, []);

    useEffect(() => {
        if (success || error) {
            setTimeout(async () => {
                dispatch(clearForgetPassword());
            }, TIMEOUT_STATUS_FORGET_PASSWORD);
        }
    }, [success, error]);

    const handleSubmit = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        e.preventDefault();
        let newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            dispatch(forgetPasswordFetch(editData));
        }

        setEditErrors(newFormErrors);
    };

    const onChange = (key: 'email', value: any, error: boolean) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    return (
        <MuiContainer fixed maxWidth="xs" disableGutters sx={{ display: 'flex', height: '100%' }}>
            <div className="Login" style={style.app}>
                {loading && <Loading />}
                <form ref={formRef} onSubmit={handleSubmit}>
                    <MuiGrid container columns={1}>
                        <MuiGrid item xs={1}>
                            <LogoBlock>
                                <Logo src={appLogo} alt="skladon background" />
                            </LogoBlock>
                        </MuiGrid>
                        <MuiGrid item xs={1}>
                            <Typography variant="h2" gutterBottom>
                                {translate('login.passwordReset')}
                            </Typography>
                        </MuiGrid>
                        <MuiGrid item xs={1}>
                            <TextField
                                value={editData.email}
                                error={editErrors.email}
                                onChange={(value, error) => onChange('email', value, error)}
                                name="email"
                                type="email"
                                label={translate('login.passwordReset.email')}
                                required
                                fullWidth
                            />
                        </MuiGrid>
                        <MuiGrid item xs={1}>
                            <LangSelect />
                            <Typography variant="body2" gutterBottom>
                                {translate('login.logInHere')}
                                <Button
                                    variant="text"
                                    fullWidth
                                    sx={{
                                        textAlign: 'left',
                                        paddingLeft: '0px',
                                        display: 'inline-block',
                                        width: 'auto',
                                        textDecoration: 'underline',
                                    }}
                                    noBackground
                                    onClick={() => props.onClose()}
                                >
                                    {translate('login.logInHereClick')}
                                </Button>
                            </Typography>
                        </MuiGrid>
                        <MuiGrid item xs={1}>
                            <Button type="submit" fullWidth>
                                {translate('login.resetPassword')}
                            </Button>
                        </MuiGrid>

                        <MuiGrid item xs={1}>
                            {success && (
                                <Fade in={success} timeout={{ enter: 1000, exit: 1000 }}>
                                    <MessageBlock type="success">
                                        <Typography align="center" display="inline">
                                            {translate('login.resetPasswordSuccess')}
                                        </Typography>
                                    </MessageBlock>
                                </Fade>
                            )}
                            {error && (
                                <Fade in={error} timeout={{ enter: 1000, exit: 1000 }}>
                                    <MessageBlock type="error">
                                        <Typography align="center" display="inline">
                                            {translate('login.resetPasswordError')}
                                        </Typography>
                                    </MessageBlock>
                                </Fade>
                            )}
                        </MuiGrid>
                    </MuiGrid>
                </form>
            </div>
        </MuiContainer>
    );
};

export default ForgetPassword;
